import { apiRequest } from './http';

/** The status/result shape every AI job poll returns; only the fields relevant to what was started are populated. */
export interface AiJobPollResult {
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  result?: string;
  message?: string;
  outline?: unknown;
  choices?: string[];
  error?: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Starts a background AI job and polls it to completion. The API never blocks a request on the
 * model — see AiFunctions.GetJobAsync — so a slow completion cannot time out the request.
 */
export async function runAiJob<T>(startPath: string, body: unknown, map: (poll: AiJobPollResult) => T): Promise<T> {
  const { jobId } = await apiRequest<{ jobId: string }>(startPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  for (;;) {
    const poll = await apiRequest<AiJobPollResult>(`/api/ai/jobs/${jobId}`);
    if (poll.status === 'succeeded') return map(poll);
    if (poll.status === 'failed') throw new Error(poll.error ?? 'AI assistance failed.');
    if (Date.now() > deadline) throw new Error('AI assistance took too long. Please try again.');
    await sleep(POLL_INTERVAL_MS);
  }
}
